import { useTransaction } from '../../../context/TransactionContext';
import { ITransaction, IUpdateTransaction } from '../../../interface/transaction';
import { useTransactionValidation } from './useTransactionValidation';
import { useTransactionPayloadBuilder } from './useTransactionPayloadBuilder';
import { useTransactionMutations } from './useTransactionMutations';
import { ITransactionParameters } from '../../../interface/parameters';

interface UseTransactionFormHandlingProps {
  transactionId: string | undefined;
  transactionDate: string;
  billableFlag: boolean;
  parameters: ITransactionParameters;
  hidePricingInfo: boolean;
  isTransactionTypeRequired: boolean;
  userType: string;
  handleBack: () => void;
  checkForTimerAndRemove: () => void;
  handleTransactionisFromMailOrAppointment: (transactionRecord: ITransaction) => Promise<void>;
}

export const useTransactionFormHandling = ({
  transactionId,
  transactionDate,
  billableFlag,
  parameters,
  hidePricingInfo,
  isTransactionTypeRequired,
  userType,
  handleBack,
  checkForTimerAndRemove,
  handleTransactionisFromMailOrAppointment,
}: UseTransactionFormHandlingProps) => {
  const { transaction } = useTransaction();

  // Use focused hooks for specific responsibilities
  const { validateFields, getFirstInvalidField } = useTransactionValidation({
    isTransactionTypeRequired,
    skillRequired: parameters?.skillRequired,
  });

  const {
    getCalculatePricesValue,
    buildCreateTransactionPayload,
    buildCreateTransactionParams,
    buildUpdateTransactionPayload,
    buildUpdateTransactionParams,
    getChangedFieldsPayload,
  } = useTransactionPayloadBuilder({
    transactionDate,
    billableFlag,
    parameters,
    hidePricingInfo,
    userType,
  });

  const {
    handleEditTransaction,
    handleCreateTransaction,
    transactionFieldError,
    clearTransactionFieldError,
    isTransactionError,
    transactionErrorMessage,
    closeTransactionErrorModal,
  } = useTransactionMutations({
    transactionId,
    handleBack,
    checkForTimerAndRemove,
    handleTransactionisFromMailOrAppointment,
  });

  const handleEditTransactionSubmit = () => {
    const calculatePricesValue = getCalculatePricesValue();
    const payload = buildUpdateTransactionPayload(calculatePricesValue);
    const params = buildUpdateTransactionParams(calculatePricesValue);
    const changedPayload = getChangedFieldsPayload(payload);

    handleEditTransaction(
      {
        ...changedPayload,
        version: payload.version,
      } as IUpdateTransaction,
      params,
    );
  };

  const handleCreateTransactionSubmit = () => {
    const calculatePricesValue = getCalculatePricesValue();
    const payload = buildCreateTransactionPayload(calculatePricesValue);
    const params = buildCreateTransactionParams(calculatePricesValue);

    handleCreateTransaction(payload, params, transaction?.activityId, transaction?.workTimeId);
  };

  return {
    validateFields,
    getFirstInvalidField,
    handleEditTransaction: handleEditTransactionSubmit,
    handleCreateTransaction: handleCreateTransactionSubmit,
    transactionFieldError,
    clearTransactionFieldError,
    isTransactionError,
    transactionErrorMessage,
    closeTransactionErrorModal,
  };
};
